import dayjs from 'dayjs';
import moment from 'moment';
import { PropTypes } from 'prop-types';
import React, { useCallback, useMemo, useEffect, useContext } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '@ge/components/button';
import { Icon, Icons } from '@ge/components/icon';
import { MiniLoader } from '@ge/components/loader';
import { Dialog } from '@ge/components/modal';
import { useNotification } from '@ge/components/notification';
import { ScrollingContainer } from '@ge/components/scrolling-container';
import { Textarea } from '@ge/components/textarea/textarea';
import { Text } from '@ge/components/typography';
import NotificationMessage from '@ge/feat-monitor/components/notification-message';
import { DateTimeFormats, NotificationType } from '@ge/models/constants';
import { DateMetaField } from '@ge/shared/components/meta-fields';
import { TaskContext } from '@ge/shared/components/tasks/task-context';
import { TaskProvider } from '@ge/shared/components/tasks/task-provider';
import { useTaskDelayScope, useEditTask } from '@ge/shared/data-hooks';
import { useLogger } from '@ge/shared/hooks';
import { getUserProfileInfo } from '@ge/shared/services/auth';
import { getDelayDuration, addUTCOffset, getDateTz } from '@ge/shared/util/time-date';
import { elevations, typography } from '@ge/tokens';
import { globalColors } from '@ge/tokens/colors';

const StyledNewTaskDialog = styled(Dialog)`
  &.hide-dialog {
    opacity: 0;
    visibility: hidden;
  }
  svg {
    fill: ${(props) => props.theme.mainNav.iconColor};
  }
`;

const ErrorText = styled(Text).attrs(() => ({
  type: typography.textTypes.body,
  level: 2,
}))`
  color: ${(props) => props.theme.dangerColor} !important;
  display: block;
  margin-bottom: 10px;
  width: 190px;
`;

const DialogScrollWrapper = styled.div`
  box-sizing: border-box;
  padding: 20px 25px;
`;

const NewTaskForm = styled.form`
  display: flex;
  flex: 1;
  width: 567px;
  height: 600px;
  max-height: calc(75vh - 101px);
`;

const NewTaskFooterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  button {
    text-transform: capitalize;
  }
`;

const FooterButtons = styled.div`
  margin-left: auto;
  button {
    &:not(:last-of-type) {
      margin-right: 5px;
    }
  }
`;

const AddDelayRow = styled.div`
  font-size: 11px;
  font-weight: 600;
  line-height: 13.2px;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #1c252d;
  padding: 20px 25px;

  .message {
    font-size: 13px;
    font-style: italic;
    font-weight: 300;
    line-height: 15.6px;
    color: var(--color-slate-80, #c5d2de);
    margin: 7px 0;
  }
  span {
    color: #999999;
    font-size: 12px;
    font-weight: 300;
    line-height: 15px;
    color: #999999;
  }
`;

const DatesRow = styled.div`
  display: flex;
  border: none;
  margin-bottom: 0px;
  padding-bottom: 12px;
  flex-direction: column;
  z-index: ${elevations.P2};
  border-bottom: 1px solid #1c252d;
  > div {
    .date-picker input {
      width: 180px;
    }
  }
  .delayDates {
    display: flex;
    flex-direction: column;
    margin: 16px 0;
    .delayDatesRow {
      display: flex;
      flex-direction: row;
    }
  }
  h3 {
    font-size: 12px;
    font-weight: 400;
    line-height: 15px;
    margin-top: 12px;
  }

  span {
    font-size: 10px;
    font-weight: 300;
    line-height: 12px;
    letter-spacing: 0.5px;
    color: #c5d2de;
    margin-top: 5px;
  }
`;

export const StyledDateContainer = styled.div`
  &:first-child {
    margin-right: 50px;
  }
  position: relative;
  &:last-of-type {
    margin-right: 0;
  }
  .label {
    color: ${(props) => props.theme.entityDetails.tasks.details.labelColor};
    margin-bottom: 5px;
  }
  .date-picker {
    display: flex;
    align-items: center;
    input {
      border-radius: 3px;
      color: ${(props) => props.theme.entityDetails.tasks.details.inputColor};
      font-size: 12px;
      width: 110px;
    }
    .value {
      margin-right: 40px;
    }
    .task-dialog-date {
      position: absolute;
      bottom: 7px;
      right: -2px;
    }
  }
`;

export const StyledTextarea = styled(Textarea)`
  resize: none !important;
  height: 90px;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    height: 37.16px;
    width: 2px;
    border-radius: 2.5px;
    background-color: #445a6c;
  }
  &::-webkit-scrollbar-track {
    height: 68px;
    width: 6px;
    border-radius: 1px;
    background-color: rgba(12, 15, 18, 0.24);
  }
`;

const ErrorIcon = styled(Icon).attrs((props) => ({
  size: 8,
  icon: Icons.DATA_NETCOMM,
  color: `${globalColors.red2} !important`,
}))`
  margin-right: 2px;
`;

export const DelayEndDialog = ({
  onClose,
  onConfirm,
  currentDelay,
  task,
  onCloseTask,
  closeTaskValues,
  onSaveTask,
  isSaveTask,
  completedDate,
}) => {
  const { t } = useTranslation(['tasks', 'general'], { useSuspense: false });
  const {
    taskState: { timezone },
  } = useContext(TaskContext);
  const userInfo = getUserProfileInfo();
  const logger = useLogger();
  const { notify, dismiss } = useNotification();
  const methods = useForm({
    mode: 'onChange',
  });
  const {
    control,
    reset,
    errors,
    getValues,
    formState: { isValid },
  } = methods;
  const currentDate = dayjs().startOf('date');
  const { fields } = useFieldArray({
    name: 'delayDates',
    control,
  });

  useEffect(() => {
    let setData = [];
    if (currentDelay?.length) {
      setData = currentDelay.map((e) => {
        return {
          startDate: e?.startDate ? new Date(e?.startDate) : null,
          endDate: e?.endDate && new Date(e?.endDate),
        };
      });
      reset({ delayDates: setData });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDelay]);

  const { isLoading: isTaskDelayLoading, data: taskDelayScopes } = useTaskDelayScope(task.id);
  const taskCompleteDate = useMemo(
    () => (completedDate ? completedDate : task.completedDateTime || currentDate),
    [],
  );
  const timeZoneCompleteDate = getDateTz(taskCompleteDate, timezone);
  const delayReasonOptions = useMemo(() => {
    const taskReason = [];
    taskDelayScopes?.forEach((c) => {
      taskReason.push({
        value: c.reason,
        label: c.reasonDesc,
      });
    });
    return taskReason;
  }, [taskDelayScopes]);

  const { edit: fetchDelay } = useEditTask({
    onSettled: onConfirm,
    onSuccess: () => {
      dismiss();
      let message = (
        <NotificationMessage
          message={t('action_buttons.edit_delay', 'DELAY UPDATED SUCCESSFULLY')}
        />
      );
      isSaveTask ? onSaveTask(closeTaskValues) : onCloseTask(closeTaskValues);
      notify({ message });
      onConfirm();
    },
    onError: (e) => {
      const message = (
        <NotificationMessage
          message={t('action_buttons.delay_update_failed', 'DELAY UPDATE FAILED')}
        />
      );
      const type = NotificationType.FAILURE;
      notify({ message, type });
      logger.error('Error updating delay', e);
    },
  });

  const getDelaySlNo = useMemo(() => task?.delayDetails?.length + 1, [task]);

  const prevDelays = useMemo(() => (task?.delayDetails ? task?.delayDetails : []), [task]);

  const onSubmit = useCallback(
    (values) => {
      const id = task?.id;
      const delays = [...task.delayDetails];
      values.delayDates.map((e, i) => {
        const { startDate, endDate } = e;
        const { index } = currentDelay[i];
        delays[index] = {
          ...delays[index],
          startDate: dayjs(startDate).format(DateTimeFormats.EVENT_DATE_TIME),
          endDate: dayjs(endDate).format(DateTimeFormats.EVENT_DATE_TIME),
        };
      });
      const editPayload = {
        properties: {
          delayDetails: [...delays],
        },
      };
      fetchDelay({ id, task: editPayload, bundledTaskIds: task?.bundledTasks });
      let message = (
        <NotificationMessage
          message={t('action_buttons.edit_inprogress', 'UPDATING DELAY')}
          isProgressLoader={true}
        />
      );
      notify({ message });
      onClose();
    },
    [
      notify,
      getDelaySlNo,
      fetchDelay,
      delayReasonOptions,
      currentDelay,
      task,
      userInfo,
      prevDelays,
      onClose,
      t,
      isSaveTask,
    ],
  );

  const formatDate = (date) => dayjs(date).format(DateTimeFormats.CREW_TIMING);

  const getFormWrapper = (data, i) => {
    const { reasonDesc, contributedByName, updatedOn, index } = currentDelay[i];
    const { startDate, endDate } = data;
    const updatedOnNew = dayjs(updatedOn).format(DateTimeFormats.NEW_TASK);
    const dates = getValues()?.delayDates;
    const delayStart = formatDate(dates ? dates[i]?.startDate : startDate);
    const delayEnd = formatDate(dates ? dates[i]?.endDate : endDate);
    const maxDate = formatDate(timeZoneCompleteDate);
    return (
      <DatesRow key={`delay-row-${i}`}>
        <h3>{reasonDesc}</h3>
        <span>{`${
          contributedByName ? `${contributedByName} - ` : ''
        }Last Updated: ${updatedOnNew}`}</span>
        <div className="delayDates">
          <div className="delayDatesRow">
            <StyledDateContainer>
              <div className="label">{t('action_buttons.delay_start_date', 'Start Date*')}</div>
              <DateMetaField
                metadata={{ required: true }}
                defaultValue={startDate}
                name={`delayDates[${i}].startDate`}
                max={maxDate}
                fieldArray={{
                  fieldName: `delayDates`,
                  errorIndex: index,
                  errorName: 'startDate',
                }}
                rules={{
                  validate: {
                    isAfterCompletedDate: (value) =>
                      moment(formatDate(value)).isSameOrBefore(maxDate),
                  },
                }}
              />
              {moment(delayStart).isAfter(maxDate) && (
                <ErrorText>
                  <ErrorIcon />
                  {t(
                    'action_buttons.error_start_delay',
                    'Delay start date must be on/before task completed date.',
                  )}
                </ErrorText>
              )}
            </StyledDateContainer>
            <StyledDateContainer>
              <div className="label">
                {t('action_buttons.end_date', 'Actual/Tentative End Date')}
              </div>
              <DateMetaField
                metadata={{ required: true }}
                defaultValue={addUTCOffset(endDate, timezone)}
                name={`delayDates[${i}].endDate`}
                min={delayStart}
                max={maxDate}
                fieldArray={{ fieldName: `delayDates`, errorIndex: i, errorName: 'endDate' }}
                rules={{
                  validate: {
                    isAfterCompletedDate: (value) =>
                      moment(formatDate(value)).isSameOrBefore(maxDate),
                  },
                }}
              />
              {!dayjs(delayEnd).isValid() && (
                <ErrorText>
                  {t(
                    'action_buttons.delay_end_date_required_error',
                    'Actual/Tentative End Date required',
                  )}
                </ErrorText>
              )}
              {moment(delayEnd).isAfter(maxDate) && (
                <ErrorText>
                  <ErrorIcon />
                  {t(
                    'action_buttons.error_end_delay',
                    'Delay end date must be on/before task completed date.',
                  )}
                </ErrorText>
              )}
            </StyledDateContainer>
          </div>
        </div>
        <span>{getDelayDuration(t, { startDate: delayStart, endDate: delayEnd })}</span>
      </DatesRow>
    );
  };

  const getTaskTemplate = useMemo(() => {
    const completionDate = dayjs(timeZoneCompleteDate).format(DateTimeFormats.NEW_TASK);
    return (
      <ScrollingContainer>
        <AddDelayRow>
          <h3>
            {currentDelay.length} {t('action_buttons.delay_active', 'ACTIVE DELAY')}
          </h3>
          <div className="message">
            {t('action_buttons.delay_msg', 'Delay end dates are mandatory to close tasks.')}
          </div>
          <span>
            {t('action_buttons.completion_msg', 'Task Completion Date')} : {completionDate}
          </span>
        </AddDelayRow>
        <DialogScrollWrapper>
          {currentDelay.length && fields.map((e, i) => getFormWrapper(e, i))}
        </DialogScrollWrapper>
      </ScrollingContainer>
    );
  }, [getFormWrapper, fields]);

  // Build the footer to pass into the Dialog component.
  const getFooter = useMemo(() => {
    return (
      <NewTaskFooterWrapper>
        <FooterButtons>
          <Button onClick={onClose}>{t('general:cancel', 'Cancel')}</Button>
          <Button primary onClick={methods.handleSubmit(onSubmit)} disabled={!isValid}>
            {isSaveTask ? t('general:save', 'Save') : t('form.close_task', 'Close Task')}
          </Button>
        </FooterButtons>
      </NewTaskFooterWrapper>
    );
  }, [t, methods, onSubmit, onClose]);

  return (
    <TaskProvider>
      <FormProvider {...methods}>
        <StyledNewTaskDialog
          isOpen={true}
          onClose={onClose}
          onConfirm={onConfirm}
          header={t('action_buttons.delay_end', 'END DELAY')}
          footer={getFooter}
          contentWidth
          padContent={false}
        >
          <NewTaskForm>
            {taskDelayScopes || !isTaskDelayLoading ? getTaskTemplate : <MiniLoader />}
          </NewTaskForm>
        </StyledNewTaskDialog>
      </FormProvider>
    </TaskProvider>
  );
};

DelayEndDialog.propTypes = {
  task: PropTypes.object,
  onConfirm: PropTypes.func,
  onCloseTask: PropTypes.func,
  closeTaskValues: PropTypes.any,
  isSaveTask: PropTypes.bool,
  onSaveTask: PropTypes.func,
  currentDelay: PropTypes.object,
  onClose: PropTypes.func,
  completedDate: PropTypes.any,
};

DelayEndDialog.defaultProps = {
  isOpen: false,
  onClose: () => null,
  onConfirm: () => null,
  currentDelay: null,
  methods: null,
  isCalledfromTaskEscalationColumn: null,
};
